/**
 * The sky as seen from inside a system.
 *
 * Every other system in the galaxy, drawn in its true direction and coloured by
 * its actual star. That makes the sky a navigation instrument rather than
 * decoration: the cluster you are about to warp to is a real patch of light in
 * a real direction, and you can line up on it by eye.
 *
 * It also fixes how isolated a system felt with only procedural noise behind
 * it. Twelve thousand suns are out there; they should be visible.
 */

import { getGalaxyMap, type SystemId } from '@interspacial/shared';
import * as THREE from 'three';

/** Where the sky sits. Arbitrary — it follows the camera and ignores depth. */
const SKY_RADIUS = 900_000;

/**
 * Distance at which a star is drawn at full brightness. Roughly a jump away, so
 * the systems you could actually reach are the ones that stand out.
 */
const NEAR_DISTANCE = 4200;

/**
 * Distance at which a system has become an unresolvable smudge.
 *
 * Rather more than a cluster's own width, so everything inside your own cluster
 * stays crisp and readable — those are the ones you actually navigate by — and
 * the blurring is reserved for what lies beyond it.
 */
const RESOLVE_DISTANCE = 26_000;

const vertexShader = /* glsl */ `
  attribute float aSize;
  attribute float aSoftness;

  varying vec3 vColor;
  varying float vSoftness;

  void main() {
    vColor = color;
    vSoftness = aSoftness;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    // Fixed pixel size: these are point sources at effectively infinite range,
    // so they must not shrink as the camera moves.
    gl_PointSize = aSize;
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;

  varying vec3 vColor;
  varying float vSoftness;

  void main() {
    // Round off the point sprite, which is square by default.
    //
    // How hard that edge is carries the distance. A star a jump away is a
    // crisp point; one across the galaxy is a smudge you cannot quite resolve,
    // and the smudge is wider than the point was. That is what atmosphere-free
    // distance actually looks like — unresolved light spreading out — and it is
    // what lets you tell "near cluster" from "far cluster" by eye instead of by
    // brightness alone, which is the whole reason for aiming by this sky.
    vec2 offset = gl_PointCoord - 0.5;
    float radius = length(offset) * 2.0;

    // Softness slides the inner stop from "almost the whole sprite is solid"
    // to "nothing is solid, it is all falloff".
    float inner = mix(0.3, 0.0, vSoftness);
    float falloff = 1.0 - smoothstep(inner, 1.0, radius);
    if (falloff <= 0.002) discard;

    // Cubed for the blurry ones, so a far patch fades right out at its rim.
    // Any visible disc edge and the illusion collapses — a dozen overlapping
    // discs read as a dozen discs, where a dozen overlapping gradients read as
    // one cloud.
    falloff *= mix(1.0, falloff * falloff, vSoftness);

    gl_FragColor = vec4(vColor * falloff, 1.0);
  }
`;

export interface SystemSky {
  points: THREE.Points;
  /** Keeps the sky centred on the viewer. Call once per frame. */
  follow: (cameraPosition: THREE.Vector3) => void;
  dispose: () => void;
}

export function createSystemSky(seed: string, systemId: SystemId): SystemSky {
  const all = getGalaxyMap(seed).flatMap((entry) => entry.systems);
  const here = all.find((system) => system.id === systemId);

  const positions: number[] = [];
  const colors: number[] = [];
  const sizes: number[] = [];
  const softness: number[] = [];
  const colour = new THREE.Color();

  for (const system of all) {
    if (!here || system.id === systemId) continue;

    const dx = system.galacticPosition.x - here.galacticPosition.x;
    const dy = system.galacticPosition.y - here.galacticPosition.y;
    const dz = system.galacticPosition.z - here.galacticPosition.z;
    const distance = Math.hypot(dx, dy, dz);
    if (distance < 1e-6) continue;

    const scale = SKY_RADIUS / distance;
    positions.push(dx * scale, dy * scale, dz * scale);

    // Inverse-square would make everything past the next cluster invisible.
    // A gentler falloff keeps the far galaxy as a faint band of light, which is
    // what a sky from inside a disc should look like — but the old exponent was
    // so gentle that a system on the other side of the galaxy was very nearly
    // as bright as one next door, and the sky read as uniform static.
    const brightness = Math.min(1, (NEAR_DISTANCE / distance) ** 0.95);

    // How far past resolving this one is, 0 (crisp) to 1 (a smear).
    const blur = Math.min(1, Math.max(0, distance / RESOLVE_DISTANCE) ** 0.5);
    softness.push(blur);

    colour.set(system.starColor);
    // Distant light also loses its colour, drifting toward the pale wash of
    // everything behind it, so the far galaxy reads as one band rather than as
    // individually coloured pinpricks.
    colour.lerp(new THREE.Color(0x9fb2d0), blur * 0.65);

    // Deliberately not vanishing: a far system is still a real place you can
    // plot a warp to, so it stays just barely present.
    const lift = 0.05 + 0.95 * brightness;
    colors.push(colour.r * lift, colour.g * lift, colour.b * lift);

    // The unresolved ones grow, hard.
    //
    // A distant cluster is not thirty faint dots — at that range you cannot
    // separate its stars at all, and what reaches you is one patch of light
    // with a shape. Getting that means each sprite spreading wide enough to
    // overlap its neighbours, so the additive blend fuses them into a single
    // form instead of leaving a scatter of pinpricks that reads as noise.
    //
    // The dimming does the balancing: these are individually faint, so a dozen
    // of them piled on top of each other come to about one soft glow rather
    // than a white blob.
    sizes.push((1.1 + brightness * 4.5) * (1 + blur * blur * 13));
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setAttribute('aSize', new THREE.Float32BufferAttribute(sizes, 1));
  geometry.setAttribute('aSoftness', new THREE.Float32BufferAttribute(softness, 1));

  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    vertexColors: true,
    // Depth off and drawn early, so the sky is behind everything without
    // participating in the depth buffer — the same trick the nebula uses, and
    // it sidesteps having to hand-implement logarithmic depth here.
    depthTest: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const points = new THREE.Points(geometry, material);
  points.renderOrder = -900;
  points.frustumCulled = false;

  return {
    points,
    follow: (cameraPosition) => points.position.copy(cameraPosition),
    dispose: () => {
      geometry.dispose();
      material.dispose();
    },
  };
}
